import { interpretarTelefonoGuardado } from "./telefonoE164";

/**
 * Sanitiza un input de teléfono: solo permite dígitos y opcionalmente
 * un "+" al inicio para código de país. Este es el formato CANÓNICO que se
 * guarda en la DB ("todo pegado", ej: `56912345678` o `+56912345678`).
 * Se usa en el `onChange` de los inputs para normalizar mientras se tipea.
 */
export const formatPhone = (value: string): string => {
  const startsWithPlus = value.startsWith("+");
  const digits = value.replace(/[^\d]/g, "");
  return startsWithPlus ? `+${digits}` : digits;
};

/**
 * Formatea un teléfono guardado (dígitos pegados, con `+` opcional) para
 * MOSTRARLO en la UI: agrupa los dígitos de a 4 contando de DERECHA a
 * IZQUIERDA, separados por espacios. El grupo más a la izquierda queda con
 * el resto (1–4 dígitos). Un `+` inicial se conserva pegado al primer grupo.
 *
 * Regla pedida por el dueño (2026-07-02): en la DB van "todo pegado"
 * (`formatPhone`); al traerlos al frontend se muestran "de atrás hacia
 * adelante" en bloques de 4, ej: `xx xxxx xxxx`.
 *
 * Ejemplos:
 *   "56912345678"  → "569 1234 5678"
 *   "+56912345678" → "+569 1234 5678"
 *   "1234567890"   → "12 3456 7890"
 *   "12345678"     → "1234 5678"
 *   "" / null      → ""
 *
 * SOLO presentación: nunca usar para guardar ni para lógica de
 * búsqueda/comparación (para eso está `formatPhone`).
 */
export const formatPhoneDisplay = (
  value: string | number | null | undefined,
): string => {
  if (value == null) return "";
  const str = String(value).trim();
  if (!str) return "";
  const startsWithPlus = str.startsWith("+");
  const digits = str.replace(/[^\d]/g, "");
  if (!digits) return startsWithPlus ? "+" : "";
  return (startsWithPlus ? "+" : "") + agruparDeA4(digits);
};

/** Agrupa dígitos de a 4 contando desde el final (el grupo de más a la izquierda queda con el resto). */
const agruparDeA4 = (digits: string): string => {
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 4) {
    groups.unshift(digits.slice(Math.max(0, end - 4), end));
  }
  return groups.join(" ");
};

/**
 * Tamaños de los grupos del número NACIONAL (sin el código de país) tal como se escribe en cada país del selector, o `null` si el
 * largo o el comienzo del número no calzan con ningún formato conocido (entonces se cae al agrupado de a 4 de siempre). La suma de
 * los tamaños devueltos es SIEMPRE el largo del número: `agruparSegun` lo comprueba y, si no cuadra, descarta el patrón.
 *
 * Son formatos de PRESENTACIÓN habituales, no una validación: ningún número se rechaza ni se corrige por no calzar acá.
 */
const PATRONES_NACIONALES: Readonly<Record<string, (nacional: string) => number[] | null>> = {
  // Chile: móvil 9 xxxx xxxx y fijo de Santiago 2 xxxx xxxx (9 dígitos: 1-4-4).
  "56": (n) => (n.length === 9 ? [1, 4, 4] : null),
  // Argentina: móvil 9 + área + número. Buenos Aires (11) tiene área de 2 dígitos: 9 11 2345 6789; el resto, de 3: 9 351 123 4567.
  "54": (n) => {
    if (n.length === 11 && n.startsWith("9")) return n.startsWith("911") ? [1, 2, 4, 4] : [1, 3, 3, 4];
    if (n.length === 10) return n.startsWith("11") ? [2, 4, 4] : [3, 3, 4];
    return null;
  },
  // Perú: móvil 912 345 678 (9 dígitos); fijo 1 234 5678 (8 dígitos).
  "51": (n) => (n.length === 9 ? [3, 3, 3] : n.length === 8 ? [1, 3, 4] : null),
  // Bolivia (8 dígitos): móvil 7123 4567; fijo 2 123 4567.
  "591": (n) => (n.length === 8 ? (/^[67]/.test(n) ? [4, 4] : [1, 3, 4]) : null),
  // Brasil: celular DDD + 9 dígitos (11) = 11 91234 5678; fijo DDD + 8 (10) = 11 1234 5678.
  "55": (n) => (n.length === 11 ? [2, 5, 4] : n.length === 10 ? [2, 4, 4] : null),
  // Colombia: celular 321 234 5678 (10); fijo 1 234 5678 (8).
  "57": (n) => (n.length === 10 ? [3, 3, 4] : n.length === 8 ? [1, 3, 4] : null),
  // Ecuador: celular 99 123 4567 (9); fijo 2 123 4567 (8).
  "593": (n) => (n.length === 9 ? [2, 3, 4] : n.length === 8 ? [1, 3, 4] : null),
  // Uruguay (8 dígitos): celular 99 123 456; fijo 2123 4567.
  "598": (n) => (n.length === 8 ? (n.startsWith("9") ? [2, 3, 3] : [4, 4]) : null),
  // Paraguay: 981 123 456 (9).
  "595": (n) => (n.length === 9 ? [3, 3, 3] : null),
  // Venezuela: 412 123 4567 (10).
  "58": (n) => (n.length === 10 ? [3, 3, 4] : null),
  // México: 55 1234 5678 (10).
  "52": (n) => (n.length === 10 ? [2, 4, 4] : null),
  // Estados Unidos y Canadá (y el Caribe del plan +1): 415 555 2671 (10).
  "1": (n) => (n.length === 10 ? [3, 3, 4] : null),
  // España: 612 345 678 (9).
  "34": (n) => (n.length === 9 ? [3, 3, 3] : null),
  // Francia: 6 12 34 56 78 (9).
  "33": (n) => (n.length === 9 ? [1, 2, 2, 2, 2] : null),
  // Alemania: móviles 151 2345 6789 (11) o 152 1234 567 (10); las áreas fijas varían tanto que se dejan al agrupado de a 4.
  "49": (n) => (/^1/.test(n) && (n.length === 10 || n.length === 11) ? [3, 4, n.length - 7] : null),
  // Italia: celular 312 345 6789 (10); fijo 06 1234 5678 (10).
  "39": (n) => (n.length === 10 ? (n.startsWith("3") ? [3, 3, 4] : n.startsWith("0") ? [2, 4, 4] : null) : null),
  // Reino Unido (10): móvil 7911 123456; Londres 20 7946 0958.
  "44": (n) => (n.length === 10 ? (n.startsWith("7") ? [4, 6] : n.startsWith("20") ? [2, 4, 4] : null) : null),
  // Portugal: 912 345 678 (9).
  "351": (n) => (n.length === 9 ? [3, 3, 3] : null),
};

/** Parte `digitos` en grupos de los tamaños dados; `null` si los tamaños no suman exactamente el largo (patrón que no aplica). */
const agruparSegun = (digitos: string, tamanos: number[]): string | null => {
  if (tamanos.reduce((suma, t) => suma + t, 0) !== digitos.length) return null;
  const grupos: string[] = [];
  let desde = 0;
  for (const t of tamanos) {
    grupos.push(digitos.slice(desde, desde + t));
    desde += t;
  }
  return grupos.join(" ");
};

/**
 * Muestra el teléfono de una persona del EQUIPO (superadministrador, administrador, aseador), que se guarda siempre con código
 * de país: el código va SEPARADO del número y el resto se agrupa como se escribe en ese país («+56 9 1234 5678»,
 * «+54 9 11 2345 6789», «+1 415 555 2671», «+55 11 91234 5678», «+44 7911 123456», «+34 612 345 678», «+351 912 345 678»). La
 * agrupación pura parte el código de países que no son Chile («+5 4911 2345 6789»), y justo ahí la persona necesita verlo entero
 * para revisar que sea el país correcto.
 *
 * Los 18 países del selector tienen su formato (`PATRONES_NACIONALES`); cualquier otro país, o un número cuyo largo no calza con
 * el formato de su país, se agrupa de a 4 desde la derecha como en `formatPhoneDisplay` (ronda F3).
 *
 * Un dato que no se puede separar —uno antiguo guardado sin código, o texto raro— se muestra igual que antes
 * (`formatPhoneDisplay`). Los teléfonos EXTERNOS (huésped, tarea, agenda) siguen usando `formatPhoneDisplay`: no tienen por qué
 * traer código y no se les adivina uno.
 *
 * SOLO presentación: no usar para guardar, buscar ni comparar.
 */
export const formatPhoneConCodigo = (value: string | number | null | undefined): string => {
  if (value == null) return "";
  const guardado = interpretarTelefonoGuardado(String(value));
  if (guardado.estado !== "completo") return formatPhoneDisplay(value);
  const tamanos = PATRONES_NACIONALES[guardado.codigoPais]?.(guardado.nacional);
  const nacional = (tamanos && agruparSegun(guardado.nacional, tamanos)) || agruparDeA4(guardado.nacional);
  return `+${guardado.codigoPais} ${nacional}`;
};
